import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { ArrowLeft, Download, CheckCircle, FileText, RefreshCw, Shield, AlertCircle, Send, Lock, Save } from "lucide-react";

const SEV_COLORS = { Critical: "#EF4444", High: "#F97316", Medium: "#F59E0B", Low: "#10B981" };
const SEV_BG = { Critical: "#FEF2F2", High: "#FFF7ED", Medium: "#FFFBEB", Low: "#ECFDF5" };
const RISK_CLR = { Low: "text-green-600 bg-green-50 border-green-200", Moderate: "text-amber-600 bg-amber-50 border-amber-200", High: "text-orange-600 bg-orange-50 border-orange-200", Critical: "text-red-600 bg-red-50 border-red-200" };

export default function ReportPreview() {
  const { inspectionId } = useParams();
  const navigate = useNavigate();
  const [inspection, setInspection] = useState(null);
  const [report, setReport] = useState(null);
  const [findings, setFindings] = useState([]);
  const [carrier, setCarrier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [downloadingConsolidated, setDownloadingConsolidated] = useState(false);
  const [downloadingInternal, setDownloadingInternal] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [uwNotes, setUwNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);

  useEffect(() => {
    Promise.allSettled([
      api.get(`/inspections/${inspectionId}`),
      api.get(`/reports/${inspectionId}`),
      api.get(`/inspections/${inspectionId}/findings`),
      api.get("/carrier/profile"),
    ]).then(([inspRes, reportRes, findingsRes, carrierRes]) => {
      if (inspRes.status === "fulfilled") setInspection(inspRes.value.data);
      if (reportRes.status === "fulfilled") setReport(reportRes.value.data);
      if (reportRes.status === "fulfilled") setUwNotes(reportRes.value.data?.underwriting_notes || "");
      if (findingsRes.status === "fulfilled") setFindings(findingsRes.value.data.filter(f => !f.dismissed));
      if (carrierRes.status === "fulfilled") setCarrier(carrierRes.value.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, [inspectionId]);

  const downloadPDF = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/reports/${inspectionId}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `RiskSurvey_Report_${inspection?.insured_name?.replace(/\s/g,"_")}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
    finally { setDownloading(false); }
  };

  const downloadInternalPDF = async () => {
    setDownloadingInternal(true);
    try {
      const res = await api.get(`/reports/${inspectionId}/internal-pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `RiskSurvey_INTERNAL_${inspection?.insured_name?.replace(/\s/g,"_")}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { console.error(e); alert("Failed to generate internal report."); }
    finally { setDownloadingInternal(false); }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.put(`/reports/${inspectionId}/underwriting-notes`, { underwriting_notes: uwNotes });
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 3000);
    } catch (e) { console.error(e); }
    finally { setSavingNotes(false); }
  };

  const downloadConsolidatedPDF = async () => {
    setDownloadingConsolidated(true);
    try {
      const res = await api.get(`/reports/consolidated/${inspection.job_order_id}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `RiskSurvey_Consolidated_Report_${inspection?.insured_name?.replace(/\s/g,"_")}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
    finally { setDownloadingConsolidated(false); }
  };

  const sendReportToInsured = async () => {
    setSendingEmail(true);
    try {
      const endpoint = inspection?.job_order_id
        ? `/reports/consolidated/${inspection.job_order_id}/send-email`
        : `/reports/${inspectionId}/send-email`;
      await api.post(endpoint, {});
      setEmailSent(true);
      setTimeout(() => setEmailSent(false), 5000);
    } catch (e) {
      alert("Failed to send email. Please check email settings in the Admin Portal.");
    } finally {
      setSendingEmail(false);
    }
  };

  if (loading) return <Layout><div className="flex items-center justify-center h-64"><RefreshCw className="w-6 h-6 animate-spin text-navy" /></div></Layout>;

  const critical = findings.filter(f => f.category === "Critical");
  const high = findings.filter(f => f.category === "High");
  const medium = findings.filter(f => f.category === "Medium");
  const low = findings.filter(f => f.category === "Low");

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-4 animate-fade-in">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(`/dashboard/review/${inspectionId}`)} className="text-slate-500 hover:text-navy transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="font-heading text-xl font-bold text-slate-800">Report Preview</h1>
              <p className="text-slate-500 text-sm">{inspection?.insured_name} — {report?.status === "finalized" ? "Finalized" : "Draft"}</p>
            </div>
          </div>
        <div className="flex items-center gap-3">
          {emailSent && (
            <span className="flex items-center gap-1.5 text-green-600 text-sm font-medium" data-testid="email-sent-indicator">
              <CheckCircle className="w-4 h-4" /> Email sent
            </span>
          )}
          <button
            data-testid="send-to-insured-btn"
            onClick={sendReportToInsured}
            disabled={sendingEmail}
            className="flex items-center gap-2 px-4 py-2.5 border border-slate-300 text-slate-600 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors shadow-sm disabled:opacity-60"
          >
            <Send className="w-4 h-4" />
            {sendingEmail ? "Sending..." : "Send to Insured"}
          </button>
          {inspection?.job_order_id && (
            <button
              data-testid="download-consolidated-btn"
              onClick={downloadConsolidatedPDF}
              disabled={downloadingConsolidated}
              className="flex items-center gap-2 px-4 py-2.5 border border-navy text-navy rounded-lg text-sm font-medium hover:bg-navy hover:text-white transition-colors shadow-sm disabled:opacity-60"
            >
              <FileText className="w-4 h-4" />
              {downloadingConsolidated ? "Generating..." : "Consolidated Report"}
            </button>
          )}
          <button
            data-testid="download-internal-btn"
            onClick={downloadInternalPDF}
            disabled={downloadingInternal}
            className="flex items-center gap-2 px-4 py-2.5 border border-red-700 text-red-700 rounded-lg text-sm font-medium hover:bg-red-700 hover:text-white transition-colors shadow-sm disabled:opacity-60"
          >
            <Lock className="w-4 h-4" />
            {downloadingInternal ? "Generating..." : "Internal Report"}
          </button>
          <button
            data-testid="download-pdf-btn"
            onClick={downloadPDF} disabled={downloading}
            className="flex items-center gap-2 px-5 py-2.5 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy-dark transition-colors shadow-sm disabled:opacity-60"
          >
            <Download className="w-4 h-4" />
            {downloading ? "Generating..." : "Download PDF"}
          </button>
        </div>
        </div>

        {/* Paper report preview */}
        <div className="report-paper rounded-xl overflow-hidden" data-testid="report-preview">
          {/* Header */}
          <div className="bg-navy px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Shield className="w-5 h-5 text-white/80" />
                  <span className="font-heading font-bold text-white text-lg">{carrier?.name || "Insurance Company"}</span>
                </div>
                <p className="text-white/50 text-xs">Loss Control Inspection Report</p>
              </div>
              <div className={`px-4 py-2 rounded-lg border ${RISK_CLR[report?.risk_level] || RISK_CLR.Low}`}>
                <div className="font-heading font-bold text-2xl">{report?.risk_score}<span className="text-sm font-normal">/100</span></div>
                <div className="text-xs font-medium">{report?.risk_level} Risk</div>
              </div>
            </div>
          </div>

          <div className="p-8 space-y-8">
            {/* Inspection Details */}
            <div className="grid grid-cols-3 gap-4">
              {[
                { label: "Insured Name", value: inspection?.insured_name },
                { label: "Property Address", value: inspection?.property_address },
                { label: "Inspection Type", value: inspection?.inspection_type?.replace("_", " ") },
                { label: "Policy Number", value: inspection?.policy_number || "N/A" },
                { label: "Ordered By", value: inspection?.ordered_by_name },
                { label: "Report Status", value: report?.status === "finalized" ? "Finalized" : "Draft" },
              ].map(item => (
                <div key={item.label} className="bg-slate-50 rounded-lg p-3">
                  <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">{item.label}</div>
                  <div className="text-sm font-medium text-slate-800 capitalize">{item.value}</div>
                </div>
              ))}
            </div>

            {/* Findings Summary */}
            <div>
              <h3 className="font-heading font-bold text-slate-800 text-base mb-3 pb-2 border-b border-slate-200">Findings Summary</h3>
              <div className="grid grid-cols-4 gap-3">
                {[["Critical", critical.length], ["High", high.length], ["Medium", medium.length], ["Low", low.length]].map(([sev, cnt]) => (
                  <div key={sev} className="rounded-lg p-3 text-center border" style={{ background: SEV_BG[sev], borderColor: `${SEV_COLORS[sev]}30` }}>
                    <div className="font-heading font-bold text-2xl" style={{ color: SEV_COLORS[sev] }}>{cnt}</div>
                    <div className="text-xs font-medium mt-1" style={{ color: SEV_COLORS[sev] }}>{sev}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Executive Summary */}
            {report?.executive_summary && (
              <div>
                <h3 className="font-heading font-bold text-slate-800 text-base mb-3 pb-2 border-b border-slate-200">Executive Summary</h3>
                <p className="text-slate-700 text-sm leading-relaxed whitespace-pre-line">{report.executive_summary}</p>
              </div>
            )}

            {/* Detailed Findings */}
            {findings.length > 0 && (
              <div>
                <h3 className="font-heading font-bold text-slate-800 text-base mb-4 pb-2 border-b border-slate-200">Detailed Findings</h3>
                <div className="space-y-3">
                  {["Critical", "High", "Medium", "Low"].map(sev =>
                    findings.filter(f => f.category === sev).map((f, idx) => (
                      <div key={f.id} className="border rounded-lg overflow-hidden" style={{ borderLeftWidth: 3, borderLeftColor: SEV_COLORS[sev] }}>
                        <div className="flex items-center gap-3 px-4 py-3 bg-slate-50">
                          <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ color: SEV_COLORS[sev], background: SEV_BG[sev] }}>{sev}</span>
                          <span className="font-medium text-slate-800 text-sm">{f.title}</span>
                          <span className="ml-auto text-xs text-slate-400">Confidence: {Math.round((f.ai_confidence || 0) * 100)}%</span>
                        </div>
                        <div className="px-4 py-3 space-y-2">
                          <p className="text-sm text-slate-700">{f.description}</p>
                          {f.recommendation && <div className="bg-blue-50 rounded-md p-2"><p className="text-xs text-blue-700"><span className="font-semibold">Recommendation:</span> {f.recommendation}</p></div>}
                          {f.reasoning && <p className="text-xs text-slate-500 italic">{f.reasoning}</p>}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Project Plan */}
            {report?.project_plan?.length > 0 && (
              <div>
                <h3 className="font-heading font-bold text-slate-800 text-base mb-4 pb-2 border-b border-slate-200">Corrective Action Project Plan</h3>
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-navy text-white">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-semibold">#</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold">Action Item</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold">Priority</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold">Timeframe</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {report.project_plan.map((item, i) => (
                        <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                          <td className="px-4 py-3 text-slate-500 font-mono text-xs">{i + 1}</td>
                          <td className="px-4 py-3 text-slate-700">{item.action}</td>
                          <td className="px-4 py-3"><span className="text-xs font-semibold" style={{ color: SEV_COLORS[item.priority] || "#10B981" }}>{item.priority}</span></td>
                          <td className="px-4 py-3 text-slate-500 text-xs">{item.due_timeframe}</td>
                          <td className="px-4 py-3"><span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{item.status || "Open"}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="pt-4 border-t border-slate-200">
              <p className="text-xs text-slate-400 text-center">
                Generated by RiskSurvey.ai on behalf of {carrier?.name}. All AI-generated findings have been reviewed by a qualified professional.
                {report?.version > 1 && ` Version ${report.version}.`}
              </p>
            </div>
          </div>
        </div>

        {/* Underwriting Notes — internal only, not in insured report */}
        <div className="bg-red-50 border border-red-200 rounded-xl p-6" data-testid="uw-notes-section">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-red-600" />
              <h3 className="font-heading font-bold text-red-800 text-sm">Underwriting Notes / Disposition</h3>
              <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-medium">Internal Only</span>
            </div>
            <div className="flex items-center gap-2">
              {notesSaved && (
                <span className="flex items-center gap-1 text-green-600 text-xs font-medium" data-testid="notes-saved-indicator">
                  <CheckCircle className="w-3.5 h-3.5" /> Saved
                </span>
              )}
              <button
                data-testid="save-uw-notes-btn"
                onClick={saveNotes}
                disabled={savingNotes}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-red-700 text-white rounded-md text-xs font-medium hover:bg-red-800 transition-colors disabled:opacity-60"
              >
                <Save className="w-3.5 h-3.5" />
                {savingNotes ? "Saving..." : "Save Notes"}
              </button>
            </div>
          </div>
          <textarea
            data-testid="uw-notes-textarea"
            value={uwNotes}
            onChange={e => setUwNotes(e.target.value)}
            placeholder="Enter underwriting disposition, renewal recommendations, endorsements required, or any internal notes for the underwriting file..."
            rows={4}
            className="w-full px-3 py-2.5 border border-red-200 rounded-lg text-sm text-slate-700 bg-white resize-none focus:outline-none focus:ring-2 focus:ring-red-300 focus:border-red-400 placeholder-slate-400"
          />
          <p className="text-xs text-red-500 mt-2">These notes appear only in the Internal Report PDF — never shared with the insured.</p>
        </div>
      </div>
    </Layout>
  );
}